/**
 * The files this policy has been, newest first: the one in force at the top,
 * every superseded upload below it, each downloadable. A version is a PDF
 * someone uploaded — not a snapshot of extracted text — because the PDF is the
 * record; the text is only how eve reads it.
 *
 * @module components/regulation/policy/PolicyVersionsTab
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronRightIcon, DownloadIcon, FileClockIcon, FileUpIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "~/lib/utils";
import { useOrgProfile } from "../../../state/orgProfile";
import {
  policyFileVersions,
  policyVersion,
  usePolicies,
  type Policy,
  type PolicyFileVersion,
} from "../../../state/policies";
import { Button } from "../../ui/button";
import { ListSkeleton } from "../LoadingSkeletons";
import { PolicyVersionUploadDialog } from "./PolicyFileActions";

const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

/** The circle avatar the reference uses; the initial is all we know. */
function ActorAvatar({ label }: { readonly label: string }) {
  return (
    // On a narrow page the note gets the avatar's width: the uploader is
    // named on the row's last line anyway.
    <span className="hidden size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary @[30rem]/policy:flex">
      {label.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Every version as one shape, current included, so the list renders one way. */
export const versionRows = (policy: Policy): ReadonlyArray<PolicyFileVersion & { current: boolean }> => {
  const history = policyFileVersions(policy);
  const current: PolicyFileVersion & { current: boolean } = {
    version: policyVersion(policy),
    filename: policy.filename,
    uploadedAt: policy.versionUploadedAt ?? policy.createdAt,
    uploadedByEmail: policy.versionUploadedByEmail ?? policy.uploadedByEmail,
    note: policy.versionNote ?? null,
    current: true,
  };
  return [current, ...[...history].reverse().map((entry) => ({ ...entry, current: false }))];
};

export function PolicyVersionsTab({
  organizationId,
  orgSegment,
  policyId,
}: {
  readonly organizationId: string | null;
  /** The `$org` route segment, for links into the version page. */
  readonly orgSegment: string;
  readonly policyId: string;
}) {
  const { snapshot, isPending, download } = usePolicies(organizationId);
  const navigate = useNavigate();
  const { canManage } = useOrgProfile(organizationId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const policy = snapshot?.policies.find((candidate) => candidate.id === policyId) ?? null;

  if (policy === null) {
    return isPending ? (
      <ListSkeleton rows={3} />
    ) : (
      <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
        <FileClockIcon className="mx-auto size-5 text-muted-foreground/60" />
        <p className="mt-3 text-sm font-medium text-foreground">Document not found</p>
      </div>
    );
  }

  const rows = versionRows(policy);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">Version history</h2>
          <p className="text-xs text-muted-foreground">
            {rows.length === 1
              ? "The original upload. Earlier files stay downloadable when a new version replaces them."
              : `${rows.length} versions · every earlier file stays downloadable.`}
          </p>
        </div>
        {canManage && (
          <Button
            type="button"
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="cursor-pointer gap-1.5"
          >
            <FileUpIcon className="size-3.5" />
            Upload new version
          </Button>
        )}
      </div>

      <ul className="space-y-3">
        {/* Each row opens the version's own page; the download button is
            the one thing on it that should not. */}
        {rows.map((row) => (
          <li
            key={row.version}
            onClick={() =>
              void navigate({
                to: "/$org/compliance/policies/$policyId/versions/$version",
                params: { org: orgSegment, policyId, version: String(row.version) },
              })
            }
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-muted/40",
              row.current ? "border-primary/30 bg-primary/[0.03]" : "border-border/60",
            )}
          >
            <ActorAvatar label={row.uploadedByEmail ?? "System"} />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2">
                <Link
                  to="/$org/compliance/policies/$policyId/versions/$version"
                  params={{ org: orgSegment, policyId, version: String(row.version) }}
                  onClick={(event) => event.stopPropagation()}
                  className="text-sm font-semibold text-foreground hover:underline"
                >
                  v{row.version}
                </Link>
                {row.current && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                    Current
                  </span>
                )}
                <span className="min-w-0 max-w-full truncate text-xs text-muted-foreground">{row.filename}</span>
              </p>
              <p className="mt-1 text-sm text-foreground">
                {row.note ?? (row.version === 1 ? "Original upload." : "New version uploaded.")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatDateTime(row.uploadedAt)}
                {row.uploadedByEmail ? ` · ${row.uploadedByEmail}` : ""}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`Download v${row.version}`}
              // Icon only on a narrow page: the label took the width the note needs.
              className="shrink-0 cursor-pointer gap-1.5 @max-[36rem]/policy:aspect-square @max-[36rem]/policy:px-0"
              onClick={(event) => {
                event.stopPropagation();
                download(policy.id, row.current ? undefined : row.version);
              }}
            >
              <DownloadIcon className="size-3.5" />
              <span className="hidden @[36rem]/policy:inline">Download</span>
            </Button>
            {/* The whole row opens the version; the chevron only says so, and gives way first. */}
            <ChevronRightIcon className="mt-2 hidden size-4 shrink-0 text-muted-foreground/60 @[30rem]/policy:block" />
          </li>
        ))}
      </ul>

      {canManage && (
        <PolicyVersionUploadDialog
          organizationId={organizationId}
          policy={policy}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
        />
      )}
    </div>
  );
}
